import { useRoute } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";

import { Settings } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import type React from "react";
import { useEffect } from "react";
import { Pressable } from "react-native";
import { Image, XStack, YStack } from "tamagui";
import { useStores } from "../../core/stores";
import { Permission } from "../../permissions/types";
import { usePlanNavigation } from "../../plans/usePlanNavigation";
import { ImageFit } from "../../ui/ImageFit";
import { AvatarSize, UserPropsAvatar } from "../../ui/UserAvatar";

export const BusinessPlanHeader: React.FC = observer(() => {
  const { businessPlansStore } = useStores();
  const route =
    useRoute<
      NativeStackScreenProps<
        ReactNavigation.RootParamList,
        "BusinessPlan"
      >["route"]
    >();

  const businessPlan = businessPlansStore.byId.get(route.params.businessUid);

  const navigation = usePlanNavigation();

  useEffect(() => {
    if (businessPlan?.title) {
      navigation.setOptions({ title: `${businessPlan.title} - Planira` });
    }
  }, [businessPlan?.title, navigation]);

  const user = businessPlan?.userInPlan;

  if (!businessPlan) {
    return null;
  }

  const showImage = businessPlan.data.customerInfo.logoUrl;

  return (
    <YStack gap="$medium" backgroundColor={"$backgroundBlock"}>
      <XStack
        alignItems="center"
        gap="$medium"
        width="100%"
        justifyContent="space-between"
        padding="$space.medium"
        paddingHorizontal={"$large"}
      >
        <YStack
          gap="$small"
          onPress={() =>
            navigation.navigate("BusinessHome", {
              businessUid: businessPlan.uid,
            })
          }
          cursor="pointer"
        >
          {!!showImage && <ImageFit height={35} assetUrl={showImage} />}
          {!showImage && (
            <Image
              objectFit="fill"
              width={225}
              height={42}
              source={require("../../../assets/planira.png")}
            />
          )}
        </YStack>

        <XStack alignItems="center" gap={"$medium"}>
          {user && (
            <Pressable
              onPress={() => {
                navigation.navigate("BusinessProfile", {
                  businessUid: businessPlan.uid,
                  userUid: user.uid,
                });
              }}
            >
              <UserPropsAvatar
                size={AvatarSize.Smaller}
                {...user.data}
                showBorder={businessPlan.checkPermission(
                  Permission.AvatarShowExtraBorder,
                )}
              />
            </Pressable>
          )}
          {businessPlan.checkPermission(Permission.AccessToAdminPage) && (
            <Pressable
              onPress={() => {
                navigation.navigate("BusinessSettings", {
                  businessUid: businessPlan.uid,
                });
              }}
            >
              <Settings size={24} />
            </Pressable>
          )}
        </XStack>
      </XStack>
    </YStack>
  );
});
